import React, { useRef } from 'react';
import { Paperclip, CheckCircle, Eye, X } from 'lucide-react';
import { authedMediaUrl } from '../utils/media';

// One upload slot (front or back). Shows staged-file / on-file state, opens a file picker on
// click, and a View link (authedMediaUrl → ?token=) for a file already on the server.
const Slot = ({ label, file, existingUrl, onChange }) => {
  const ref = useRef(null);
  const has = !!file || !!existingUrl;
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { e.target.value = ''; return; } // silently reject oversize
    onChange(f);
  };
  return (
    <div className={`rounded-lg border-2 border-dashed p-2.5 transition-colors ${has ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 hover:border-emerald-300'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-600">{label}</span>
        {has && <CheckCircle className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
      </div>
      <input ref={ref} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} />
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Paperclip className="h-3.5 w-3.5" />
          {file || existingUrl ? 'Replace' : 'Upload'}
        </button>
        {file ? (
          <button
            type="button"
            onClick={() => { onChange(null); if (ref.current) ref.current.value = ''; }}
            className="p-1.5 rounded-md text-gray-400 hover:text-red-500"
            title="Remove staged file"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : existingUrl ? (
          <a
            href={authedMediaUrl(existingUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50"
            title="View the file on record"
          >
            <Eye className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>
      {file && (
        <p className="text-[10px] text-gray-500 mt-1 truncate" title={file.name}>{file.name} · uploads on save</p>
      )}
    </div>
  );
};

// Front + back CNIC document uploader. Files are staged here and uploaded by the parent form
// after the record is saved. `existingFrontUrl`/`existingBackUrl` are the URLs already on the
// record (Edit mode) so the View link works without re-uploading.
const CnicDocUpload = ({
  frontFile, backFile, onFront, onBack, existingFrontUrl, existingBackUrl,
  label = 'CNIC Document',
}) => (
  <div>
    <div className="text-sm font-semibold text-gray-700 mb-1.5">
      {label}
      <span className="text-xs font-normal text-gray-400 ml-1.5">front &amp; back · PDF/JPG/PNG, max 5 MB</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Slot label="Front side" file={frontFile} existingUrl={existingFrontUrl} onChange={onFront} />
      <Slot label="Back side" file={backFile} existingUrl={existingBackUrl} onChange={onBack} />
    </div>
  </div>
);

export default CnicDocUpload;
